"use client";

import { Button } from "@/components/ui/button";
import { APP_PAGES_COPY } from "@/app/pages.copy";
import { PlusIcon, CircleIcon, XIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { reportObservabilityClientError } from "@/components/observability/client-error-reporter";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

function ErrorPage() {
  const router = useRouter();
  const [loadingBack, setLoadingBack] = useState(false);

  const handleBack = async () => {
    setLoadingBack(true);
    router.back();
  };

  return (
    <div
      className="flex items-center justify-center bg-background min-h-screen"
      data-testid="app-error-page"
    >
      <div className="text-center px-4 max-w-md">
        <div className="relative mb-8">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-48 h-24 bg-primary rounded-full filter blur-xl opacity-70"></div>
          </div>
          <div className="relative">
            <h1 className="text-9xl font-bold text-foreground">500</h1>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <CircleIcon className="text-primary w-4 h-4 absolute top-0 left-1/4" />
            <PlusIcon className="text-primary w-4 h-4 absolute top-1/4 right-1/4" />
            <CircleIcon className="text-primary/40 w-3 h-3 absolute bottom-1/4 left-1/3" />
            <XIcon className="text-primary w-4 h-4 absolute bottom-0 right-1/3" />
            <div className="w-12 h-1 bg-primary absolute -bottom-4 left-1/2 transform -translate-x-1/2"></div>
            <div className="w-12 h-1 bg-primary absolute -bottom-4 left-1/4"></div>
            <div className="w-12 h-1 bg-primary absolute -bottom-4 right-1/4"></div>
          </div>
        </div>
        <h2 className="text-2xl font-semibold text-muted-foreground mb-4">
          {APP_PAGES_COPY.error.title}
        </h2>
        <p className="text-muted-foreground mb-8">
          {APP_PAGES_COPY.error.description}
        </p>

        <div className="flex gap-2 justify-center">
          <Button
            variant="default"
            className="w-full sm:w-auto"
            onClick={handleBack}
            loading={loadingBack}
          >
            {APP_PAGES_COPY.error.back}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function GlobalError({ error }: GlobalErrorProps) {
  useEffect(() => {
    console.error(error);
    void reportObservabilityClientError({
      message: error.message,
      stack: error.stack,
      logSnapshot: {
        type: "next.global-error",
        digest: error.digest,
      },
    });
  }, [error]);

  return <ErrorPage />;
}
